<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Navigation } from 'swiper';
import PartnerServicePreview from './PartnerServicePreview.vue';
import { breakpoints } from '@glow/shared/lib/breakpoints';
import { LeftArrowIcon, RightArrowIcon } from '@glow/shared/components/icons';
import { type FragmentType, graphql } from '@/gql';

const partnerServiceFragment = graphql(/* GraphQL */ `
  fragment PartnerServicePreviewList_PartnerServiceFragment on PartnerService {
    id
    parameters {
      id
    }
    ...PartnerServicePreview_PartnerServiceFragment
  }
`);

const props = withDefaults(
  defineProps<{
    services?: FragmentType<typeof partnerServiceFragment>[];
  }>(),
  { services: () => [] }
);

const services = useFragmentArrayData(
  partnerServiceFragment,
  () => props.services
);

const withArrows = computed(() => props.services.length >= 4);
</script>

<template>
  <Swiper
    :modules="[Navigation]"
    :navigation="{
      prevEl: '.slider__prev',
      nextEl: '.slider__next',
    }"
    :breakpoints="{
      [breakpoints['xs']]: {
        spaceBetween: 12,
        slidesPerView: 1.5,
      },
      [breakpoints['sm']]: {
        spaceBetween: 24,
        slidesPerView: 2.25,
      },
      [breakpoints['xl']]: {
        spaceBetween: 24,
        slidesPerView: 3,
      },
    }"
    :class="['slider', { 'slider_with-arrows': withArrows }]"
  >
    <SwiperSlide v-for="service in services" :key="service.id" class="h-auto">
      <PartnerServicePreview class="h-100" can-be-liked :service="service" />
    </SwiperSlide>
    <AppButton
      class="slider__prev shadow"
      variant="link"
      border-radius="full"
      :icon-end="LeftArrowIcon"
    />
    <AppButton
      class="slider__next shadow"
      variant="link"
      border-radius="full"
      :icon-end="RightArrowIcon"
    />
  </Swiper>
</template>

<style lang="scss" scoped>
.slider {
  height: auto !important;

  .swiper-button-disabled {
    display: none;
  }

  &__prev,
  &__next {
    position: absolute;
    padding: var(--app-spacing-2) !important;
    top: 45% !important;
    z-index: 2;
    color: var(--app-color-black-tertiary);
    background-color: #fff !important;

    @include media-breakpoint-down(sm) {
      display: none;
    }
  }

  &__prev {
    left: 0;
    margin-left: calc(var(--app-spacing-6) * -1) !important;
  }

  &__next {
    right: 0;
    margin-right: calc(var(--app-spacing-6) * -1) !important;
  }
}
</style>
